import { z } from 'zod';
import { stringArraySchema, timestampSchema } from './shared';

export const conversationStatusEnum = z.enum(['active', 'resolved', 'urgent']);

export const noteEntrySchema = z.object({
  id: z.string(),
  body: z.string(),
  createdAt: z.string(),
  messageId: z.string().nullable().optional(),
});

export const conversationRowSchema = z.object({
  id: z.string().min(1),
  leadId: z.string().nullable(),
  leadAttachedAt: timestampSchema.nullable(),
  visitorName: z.string().nullable(),
  visitorEmail: z.string().nullable(),
  visitorMobile: z.string().nullable(),
  ipAddress: z.string().nullable(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullable(),
  status: conversationStatusEnum,
  notes: z.array(noteEntrySchema),
  tags: stringArraySchema,
  startedAt: timestampSchema,
  lastMessageAt: timestampSchema,
});

// Public POST body for /api/conversations (start a new conversation).
export const conversationCreateSchema = z.object({
  id: z.string().min(8).max(128).optional(),
});

// Public POST body for /api/conversations/:id/messages.
export const conversationMessageSchema = z.object({
  id: z.string().min(8).max(128),
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(50_000),
});

// Public POST body for /api/conversations/:id/lead (attach a lead).
export const conversationLeadSchema = z.object({
  fullName: z.string().min(1).max(200),
  email: z.string().email().max(320),
  mobile: z.string().min(3).max(40),
  consent: z.boolean().optional(),
  source: z.string().optional(),
});

// Admin PATCH body. Moved verbatim from app/server/routes/conversations.ts.
export const conversationPatchSchema = z.object({
  status: conversationStatusEnum.optional(),
  flagged: z.boolean().optional(),
  flaggedReason: z.string().nullable().optional(),
  notes: z.array(noteEntrySchema).optional(),
  tags: z.array(z.string()).optional(),
});

export type ConversationRow = z.infer<typeof conversationRowSchema>;
export type ConversationCreate = z.input<typeof conversationCreateSchema>;
export type ConversationMessageInput = z.input<typeof conversationMessageSchema>;
export type ConversationLeadInput = z.input<typeof conversationLeadSchema>;
export type ConversationPatch = z.input<typeof conversationPatchSchema>;
export type NoteEntry = z.infer<typeof noteEntrySchema>;
